/** Account-wide Starweave catalog and exact server-safe pricing. Prototype saves are never imported. */
export type StarweaveBranchId = 'command' | 'attunement' | 'growth' | 'fortune' | 'resolve'
export type StarweaveTier = 1 | 2 | 3 | 4 | 'nova'

export interface StarweaveBranch {
  id: StarweaveBranchId
  name: string
  description: string
  icon: string
  color: string
}

export interface StarweaveNode {
  id: string
  name: string
  description: string
  branchId: StarweaveBranchId | null
  branchIds: readonly StarweaveBranchId[]
  tier: StarweaveTier
  baseCost: number
  requires: { all?: readonly string[]; any?: readonly string[] }
}

export const STARWEAVE_BRANCHES: readonly StarweaveBranch[] = [
  { id: 'command', name: 'Command', description: 'Choose your team size, build momentum, and keep assignments moving.', icon: '⚑', color: '#dfa06b' },
  { id: 'attunement', name: 'Attunement', description: 'Grow your elements through bold assignments and better training choices.', icon: '✧', color: '#80cacc' },
  { id: 'growth', name: 'Growth', description: 'Learn from risks and setbacks. Let veterans bring new recruits along.', icon: '❋', color: '#a5cb83' },
  { id: 'fortune', name: 'Fortune', description: 'Land more Windfalls, collect bigger payouts, and find the right relics.', icon: '✦', color: '#e9c978' },
  { id: 'resolve', name: 'Resolve', description: 'Survive close calls and turn a failed assignment into a recovery.', icon: '◇', color: '#b8a0dd' },
]

// Raise the entry price without inflating the old branch/Nova endpoints.
const TIER_COSTS = { 1: 500, 2: 650, 3: 800, 4: 1_000 } as const

function branchNodes(
  branchId: StarweaveBranchId,
  names: readonly [string, string, string, string, string],
  descriptions: readonly [string, string, string, string, string],
): StarweaveNode[] {
  const stages = ['spark', 'left', 'right', 'signature', 'anchor'] as const
  const tiers = [1, 2, 2, 3, 4] as const
  return stages.map((stage, index) => {
    const tier = tiers[index]!
    const requires: StarweaveNode['requires'] = stage === 'spark' ? {}
      : stage === 'signature' ? { any: [`${branchId}-left`, `${branchId}-right`] }
        : { all: [`${branchId}-${stage === 'anchor' ? 'signature' : 'spark'}`] }
    return {
      id: `${branchId}-${stage}`,
      name: names[index]!, description: descriptions[index]!,
      branchId, branchIds: [branchId], tier, baseCost: TIER_COSTS[tier], requires,
    }
  })
}

function nova(
  id: string, name: string, description: string,
  branchIds: readonly [StarweaveBranchId, StarweaveBranchId],
): StarweaveNode {
  return {
    id, name, description, branchId: null, branchIds, tier: 'nova', baseCost: 5_000,
    requires: { all: branchIds.map(branch => `${branch}-anchor`) },
  }
}

export const STARWEAVE_NODES: readonly StarweaveNode[] = [
  ...branchNodes('command',
    ['Rolling Momentum', 'Lone Courier', 'Coordinated Arrival', 'Supply Depot', 'Ready Team'],
    [
      'Each node, a creature\'s first successful non-training mission grants it +1 Fight for the rest of the node.',
      'Each node, a creature\'s first solo dispatch grants it +1 Speed for the rest of the node.',
      'When three or more creatures travel together, the slowest gain +1 Speed for that assignment, including the return journey. If several tie for slowest, all of them receive the bonus.',
      'Gold missions have +1 creature slot. Your first successful gold mission each node readies every creature\'s Surge.',
      'The first creature dispatched each node gains +1 Speed and +1 Fight for the whole node. If a team leaves together, the first creature selected gets the bonus.',
    ]),
  ...branchNodes('attunement',
    ['A Wider Spectrum', 'Deep Affinity', 'Calculated Risk', 'Skill Reroll', 'Balanced Heart'],
    [
      'See one additional random candidate in the starting draft. Your starting team size stays the same.',
      'On assignments with exactly two creatures, each creature that naturally has only one element gains +1 to that element for the assignment.',
      'Winning a mission dispatched below 60% success gives each teammate +1 to its lowest elemental stat above zero for the run. Up to three wins per node; ties boost one stat.',
      'Once per run, reroll all skills offered by a Training Mission before choosing. You still learn one skill. Works with Broader Curriculum.',
      'Your first drafted creature gains +1 to its lowest element above zero, and +1 to its highest element while Surging. Each bonus affects only one element when tied. Both can affect the same element.',
    ]),
  ...branchNodes('growth',
    ['Focused Training', 'Paired Currents', 'Lessons in Defeat', 'Catch-Up Lessons', 'Mentorship'],
    [
      'Completing a Training Mission grants the creature 5 extra XP.',
      'Your first successful two-creature mission each node grants both creatures 5 extra XP.',
      'Your first failed assigned mission each node grants its creatures 10 extra XP.',
      'When your first new recruit each act joins, it gains 2 XP for each level it trails your highest-level creature, up to 6 XP. Starting draft picks do not count.',
      'A creature leveling up grants 3 XP to your lowest-level creature, up to 9 XP per node.',
    ]),
  ...branchNodes('fortune',
    ['Finder\'s Fee', 'Bargain Hunter', 'Lucky Streak', 'Second Look', 'Jackpot'],
    [
      'Gain +5% Windfall chance. Windfalls award 5 extra gold.',
      'Your first relic bought from a shop each act returns up to 10 gold after purchase, limited to the price you paid.',
      'Gain +5% Windfall chance. Your first Windfall each node awards 10 extra gold.',
      'Once per act, reroll a relic selection before choosing. The new offer replaces the old one; rerolling does not grant an additional relic.',
      'Gain +10% Windfall chance. Double the bonus gold earned from Windfalls.',
    ]),
  ...branchNodes('resolve',
    ['Reinforced Camp', 'Controlled Failure', 'Recovery Rations', 'Last Reserve', 'Regroup'],
    [
      'Begin each run with 5 additional maximum HP and 5 additional current HP.',
      'The first failed assigned mission each node deals 1 less damage, to a minimum of 0.',
      'Heal extra HP equal to your highest creature level at your first rest stop each act and when entering a new act.',
      'Once per act, when damage leaves you alive below 25% HP, heal HP equal to your highest creature level.',
      'Once per node, your first successful non-training mission after a failed assignment heals HP equal to your highest creature level and removes 1 injury from each successful teammate.',
    ]),
  nova('nova-company', 'Expanded Company',
    'Draft one additional creature at the start of every run and gain one additional starting roster slot.',
    ['command', 'attunement']),
  nova('nova-guiding', 'Broader Curriculum',
    'Once per act, a Training Mission can teach two of its offered skills instead of one. Pick the second skill before dispatch; it is used up when the training succeeds. Shop training is unchanged.',
    ['attunement', 'growth']),
  nova('nova-discovery', 'Master Collector',
    'Once per act, pick one extra relic from a relic selection. Element Charms count double for every creature, and with all five Charms every creature gains +2 Speed.',
    ['growth', 'fortune']),
  nova('nova-dawn', 'Second Dawn',
    'Once per run, prevent lethal damage, recover to full HP, and cure every creature\'s injuries.',
    ['fortune', 'resolve']),
  nova('nova-homeward', 'Homeward Star',
    'All creatures gain +2 Speed. Each node, remove 1 injury from every member of the first team to return from a failed assigned mission.',
    ['resolve', 'command']),
]


/** 🔓 v6.85 (per Javi): the Starweave opens once Ascension 5 is unlocked. */
export const STARWEAVE_UNLOCK_ASCENSION = 5

const PURCHASE_NUMERATOR = 11
const PURCHASE_DENOMINATOR = 10
export const STARWEAVE_PURCHASE_MULTIPLIER = PURCHASE_NUMERATOR / PURCHASE_DENOMINATOR
export const STARWEAVE_NOVA_MULTIPLIER = 2
export const STARWEAVE_MAX_PRICE = Number.MAX_SAFE_INTEGER
export const STARWEAVE_CACHE_KEY = 'elemental-dispatch-starweave-account-v1'
export const STARWEAVE_LOCAL_KEY = 'elemental-dispatch-starweave-local-v1'
export const STARWEAVE_PENDING_KEY = 'elemental-dispatch-starweave-pending-v1'

const nodesById = new Map(STARWEAVE_NODES.map(node => [node.id, node]))

export interface StarweavePurchase { nodeId: string; paid: number }
export interface StarweaveAccount {
  schema: 1
  generation: string
  revision: number
  purchases: StarweavePurchase[]
}
export type StarweaveError = 'unknown-node' | 'already-owned' | 'requirements' | 'funds'
  | 'nothing-owned' | 'conflict' | 'price-changed' | 'invalid-state' | 'request-mismatch'
  | 'transferred' | 'resetting' | 'offline' | 'signin' | 'busy' | 'run-active'
export interface StarweaveRequest {
  requestId: string
  generation: string
  expectedRevision: number
  kind: 'buy' | 'refund'
  nodeId?: string
  expectedPrice?: number
}
export interface StarweaveResponse {
  ok: boolean
  reason?: StarweaveError
  account: StarweaveAccount
  balance: number
  amount: number
  replayed?: boolean
}

export function emptyStarweaveAccount(generation: string): StarweaveAccount {
  return { schema: 1, generation, revision: 0, purchases: [] }
}
export function normalizeStarweaveOwned(raw: unknown): string[] {
  return Array.isArray(raw) ? [...new Set(raw.filter((id): id is string => typeof id === 'string' && nodesById.has(id)))] : []
}
export function ownedStarweaveIds(account: StarweaveAccount): string[] {
  return account.purchases.map(purchase => purchase.nodeId)
}
export function requirementsMet(node: StarweaveNode, owned: readonly string[]): boolean {
  const learned = new Set(owned)
  return (node.requires.all ?? []).every(id => learned.has(id))
    && (!node.requires.any?.length || node.requires.any.some(id => learned.has(id)))
}
export function priceMultiplier(owned: readonly string[]): number {
  return STARWEAVE_PURCHASE_MULTIPLIER ** normalizeStarweaveOwned(owned).length
}
/** Exact ceil(base × (11/10)^purchases × 2^ownedNovas); no floating-point drift. */
export function cost(node: StarweaveNode, owned: readonly string[]): number {
  if (!Number.isSafeInteger(node.baseCost) || node.baseCost < 0) return STARWEAVE_MAX_PRICE
  const ids = normalizeStarweaveOwned(owned)
  const count = BigInt(ids.length)
  const novas = node.tier === 'nova' ? ids.filter(id => nodesById.get(id)?.tier === 'nova').length : 0
  const denominator = BigInt(PURCHASE_DENOMINATOR) ** count
  const numerator = BigInt(node.baseCost) * BigInt(PURCHASE_NUMERATOR) ** count * BigInt(STARWEAVE_NOVA_MULTIPLIER) ** BigInt(novas)
  const rounded = (numerator + denominator - BigInt(1)) / denominator
  return rounded >= BigInt(STARWEAVE_MAX_PRICE) ? STARWEAVE_MAX_PRICE : Number(rounded)
}
export function validStarweaveGeneration(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{12,80}$/.test(value)
}
/**
 * This validates SERVER ledger structure, not client ownership claims. Historical
 * prices are retained rather than recalculated: tuning and anonymous-account
 * merges must never change the amount somebody actually paid.
 */
export function validateStarweaveAccount(raw: unknown): StarweaveAccount | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const value = raw as Partial<StarweaveAccount>
  if (value.schema !== 1 || !validStarweaveGeneration(value.generation)
    || !Number.isSafeInteger(value.revision) || value.revision! < 0
    || !Array.isArray(value.purchases) || value.purchases.length > STARWEAVE_NODES.length) return null
  const owned: string[] = []
  const purchases: StarweavePurchase[] = []
  let total = 0
  for (const purchase of value.purchases) {
    if (!purchase || typeof purchase !== 'object' || typeof purchase.nodeId !== 'string'
      || !Number.isSafeInteger(purchase.paid) || purchase.paid <= 0) return null
    const node = nodesById.get(purchase.nodeId)
    if (!node || owned.includes(node.id) || !requirementsMet(node, owned)) return null
    total += purchase.paid
    if (!Number.isSafeInteger(total)) return null
    owned.push(node.id)
    purchases.push({ nodeId: node.id, paid: purchase.paid })
  }
  return { schema: 1, generation: value.generation, revision: value.revision!, purchases }
}
export interface StarweaveRepair {
  account: StarweaveAccount
  /** Stardust handed back for purchases the catalog no longer recognises. */
  refunded: number
  dropped: string[]
  repaired: boolean
}
/**
 * 🩹 v6.83: the lenient read. A stored purchase the CURRENT catalog no longer
 * recognises (renamed node, tightened prerequisite, duplicate, illegible price)
 * is dropped and what it paid is handed back, in stored order so a dropped
 * prerequisite cascades to the stars that leaned on it. A valid ledger passes
 * through untouched. Throwing here used to 503 every read, buy, refund and
 * redeem for a player holding an affected star, with no migration path.
 */
export function repairStarweaveAccount(raw: unknown, generation: string): StarweaveRepair {
  const valid = validateStarweaveAccount(raw)
  if (valid) return { account: valid, refunded: 0, dropped: [], repaired: false }
  const value = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Partial<StarweaveAccount>
  const owned: string[] = []
  const purchases: StarweavePurchase[] = []
  const dropped: string[] = []
  let refunded = 0
  let total = 0
  for (const purchase of Array.isArray(value.purchases) ? value.purchases : []) {
    const entry = (purchase && typeof purchase === 'object' ? purchase : {}) as Partial<StarweavePurchase>
    const paid = Number.isSafeInteger(entry.paid) && entry.paid! > 0 ? entry.paid! : 0
    const node = typeof entry.nodeId === 'string' ? nodesById.get(entry.nodeId) : undefined
    if (node && paid > 0 && !owned.includes(node.id) && requirementsMet(node, owned) && Number.isSafeInteger(total + paid)) {
      owned.push(node.id)
      purchases.push({ nodeId: node.id, paid })
      total += paid
      continue
    }
    dropped.push(typeof entry.nodeId === 'string' ? entry.nodeId : 'unknown')
    if (Number.isSafeInteger(refunded + paid)) refunded += paid
  }
  const revision = Number.isSafeInteger(value.revision) && value.revision! >= 0 && value.revision! < Number.MAX_SAFE_INTEGER
    ? value.revision! + 1 : 0
  return {
    account: { schema: 1, generation: validStarweaveGeneration(value.generation) ? value.generation : generation, revision, purchases },
    refunded, dropped, repaired: true,
  }
}
/**
 * 🔒 v6.83 ("bound", per Javi): a run keeps the stars it set out with until it
 * ends, so the Stardust bound in them cannot be unbound while that run is in
 * progress. Reads the run object of a cloud save (or the device's own run).
 * Daily and puzzle sessions never bind stars; a finished run releases them.
 */
export function runBindsStarweave(run: unknown): boolean {
  if (!run || typeof run !== 'object' || Array.isArray(run)) return false
  const state = run as { mode?: unknown; puzzleLevel?: unknown; defeated?: unknown; bossDefeated?: unknown; starweave?: unknown }
  if (state.mode === 'daily' || typeof state.puzzleLevel === 'number' || state.defeated === true || state.bossDefeated === true) return false
  const bound = state.starweave && typeof state.starweave === 'object' ? (state.starweave as { owned?: unknown }).owned : undefined
  return normalizeStarweaveOwned(bound).length > 0
}
export function refundableStardust(account: StarweaveAccount): number {
  const valid = validateStarweaveAccount(account)
  return valid ? valid.purchases.reduce((sum, purchase) => sum + purchase.paid, 0) : 0
}
export function parseStarweaveRequest(raw: unknown, kind: 'buy' | 'refund'): StarweaveRequest | null {
  if (!raw || typeof raw !== 'object') return null
  const body = raw as Partial<StarweaveRequest>
  if (typeof body.requestId !== 'string' || !/^[A-Za-z0-9_-]{12,100}$/.test(body.requestId)
    || !validStarweaveGeneration(body.generation)
    || !Number.isSafeInteger(body.expectedRevision) || body.expectedRevision! < 0) return null
  if (kind === 'buy' && (typeof body.nodeId !== 'string' || body.nodeId.length > 80
    || !Number.isSafeInteger(body.expectedPrice) || body.expectedPrice! < 1)) return null
  return {
    requestId: body.requestId, generation: body.generation, expectedRevision: body.expectedRevision!, kind,
    ...(kind === 'buy' ? { nodeId: body.nodeId!, expectedPrice: body.expectedPrice! } : {}),
  }
}
export function sameStarweaveRequest(a: StarweaveRequest, b: StarweaveRequest): boolean {
  return a.kind === b.kind && a.generation === b.generation && a.expectedRevision === b.expectedRevision
    && a.nodeId === b.nodeId && a.expectedPrice === b.expectedPrice
}
/** Pure settlement plan; the server commits wallet + ownership together in a transaction. */
export function settleStarweave(account: StarweaveAccount, balance: number, request: StarweaveRequest): StarweaveResponse {
  const reject = (reason: StarweaveError): StarweaveResponse => ({ ok: false, reason, account, balance, amount: 0 })
  if (!validateStarweaveAccount(account) || !Number.isSafeInteger(balance) || balance < 0
    || account.revision >= Number.MAX_SAFE_INTEGER) return reject('invalid-state')
  if (request.generation !== account.generation || request.expectedRevision !== account.revision) return reject('conflict')
  const owned = ownedStarweaveIds(account)
  if (request.kind === 'refund') {
    if (!owned.length) return reject('nothing-owned')
    const amount = refundableStardust(account)
    if (!Number.isSafeInteger(balance + amount)) return reject('invalid-state')
    return { ok: true, amount, balance: balance + amount,
      account: { ...account, revision: account.revision + 1, purchases: [] } }
  }
  const node = nodesById.get(request.nodeId ?? '')
  if (!node) return reject('unknown-node')
  if (owned.includes(node.id)) return reject('already-owned')
  if (!requirementsMet(node, owned)) return reject('requirements')
  const price = cost(node, owned)
  if (request.expectedPrice !== price) return reject('price-changed')
  if (balance < price) return reject('funds')
  return { ok: true, amount: price, balance: balance - price,
    account: { ...account, revision: account.revision + 1,
      purchases: [...account.purchases, { nodeId: node.id, paid: price }] } }
}
/**
 * Both accounts are server-validated. Keep target ownership and append unique
 * source purchases. A duplicate source purchase is refunded exactly once by the
 * surrounding wallet-transfer transaction; source ownership is then drained.
 */
export function mergeStarweaveAccounts(source: StarweaveAccount, target: StarweaveAccount) {
  if (!validateStarweaveAccount(source) || !validateStarweaveAccount(target)
    || source.revision >= Number.MAX_SAFE_INTEGER || target.revision >= Number.MAX_SAFE_INTEGER) {
    throw new Error('Invalid Starweave transfer state')
  }
  const purchases = target.purchases.map(p => ({ ...p }))
  const owned = new Set(purchases.map(p => p.nodeId))
  let duplicateRefund = 0
  for (const purchase of source.purchases) {
    if (owned.has(purchase.nodeId)) duplicateRefund += purchase.paid
    else { purchases.push({ ...purchase }); owned.add(purchase.nodeId) }
  }
  const merged = { ...target, revision: target.revision + 1, purchases }
  if (!validateStarweaveAccount(merged) || !Number.isSafeInteger(duplicateRefund)) throw new Error('Invalid Starweave transfer total')
  return { source: { ...source, revision: source.revision + 1, purchases: [] }, target: merged, duplicateRefund }
}
